// src/components/Radiologist/RequestLabTestDialog.jsx
// Dialog for radiologists/clinicians to request a lab test from PACS report form or worklist
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonBase,
  TextField,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close,
  Science as ScienceIcon,
  Send as SendIcon,
  Add as AddIcon,
  Check as CheckIcon,
  LocationOn as LocationOnIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { referralService } from '../../services/lis';

// Pre-defined lab test catalog with categories
const PREDEFINED_TESTS = [
  { label: 'Tissue Biopsy', value: 'Tissue Biopsy' },
  { label: 'Fine Needle Aspiration (FNA)', value: 'Fine Needle Aspiration' },
  { label: 'Core Needle Biopsy', value: 'Core Needle Biopsy' },
  { label: 'Histopathology', value: 'Histopathology' },
  { label: 'Cytology', value: 'Cytology' },
  { label: 'Immunohistochemistry (IHC)', value: 'Immunohistochemistry' },
  { label: 'Blood Panel (CBC)', value: 'Blood Panel (CBC)' },
  { label: 'Blood Chemistry', value: 'Blood Chemistry' },
  { label: 'Tumour Markers', value: 'Tumour Markers' },
  { label: 'Coagulation Profile (PT/INR)', value: 'Coagulation Profile' },
  { label: 'Microbiology Culture', value: 'Microbiology Culture' },
  { label: 'Urinalysis', value: 'Urinalysis' },
  { label: 'Genetic Testing', value: 'Genetic Testing' },
];

// Anatomic Body Regions & Specific Sites mapping (matching RIS Clinical Workflow)
const ANATOMIC_REGIONS = {
  Chest: {
    label: 'Chest',
    sites: ['Lungs', 'Pleura', 'Mediastinum', 'Ribs / Chest Wall', 'Thymus', 'Heart / Pericardium', 'Other']
  },
  Abdomen: {
    label: 'Abdomen',
    sites: ['Liver', 'Gallbladder / Biliary', 'Pancreas', 'Kidneys', 'Spleen', 'Adrenal Glands', 'Peritoneum / Mesentery', 'Other']
  },
  Pelvis: {
    label: 'Pelvis',
    sites: ['Prostate', 'Bladder', 'Uterus', 'Ovaries / Adnexa', 'Pelvic Lymph Nodes', 'Rectum', 'Other']
  },
  'Head & Neck': {
    label: 'Head & Neck',
    sites: ['Brain', 'Thyroid', 'Cervical Lymph Node', 'Parotid / Salivary', 'Nasopharynx', 'Oral Cavity', 'Orbit / Eye', 'Other']
  },
  Spine: {
    label: 'Spine',
    sites: ['Cervical Spine', 'Thoracic Spine', 'Lumbar Spine', 'Sacrum / Coccyx', 'Paraspinal Soft Tissue', 'Other']
  },
  Breast: {
    label: 'Breast',
    sites: ['Left Breast - Upper Outer', 'Left Breast - Other', 'Right Breast - Upper Outer', 'Right Breast - Other', 'Axillary Lymph Node', 'Bilateral', 'Other']
  },
  'Upper Extremity': {
    label: 'Upper Extremity',
    sites: ['Shoulder', 'Axilla', 'Arm / Humerus', 'Elbow', 'Forearm', 'Wrist', 'Hand', 'Other']
  },
  'Lower Extremity': {
    label: 'Lower Extremity',
    sites: ['Groin / Inguinal', 'Thigh / Femur', 'Knee', 'Leg / Tibia', 'Ankle', 'Foot', 'Other']
  },
  Other: {
    label: 'Other Region',
    sites: ['Specify Below']
  }
};

const URGENCY_OPTIONS = [
  { label: 'Routine', value: 'routine' },
  { label: 'Urgent', value: 'urgent' },
  { label: 'STAT (Emergency)', value: 'stat' },
];

export default function RequestLabTestDialog({
  open,
  onClose,
  patientId,
  patientName,
  studyUID,
  isDark: propIsDark
}) {
  const { mode } = useThemeMode();
  const isDark = propIsDark !== undefined ? propIsDark : mode === 'dark';

  // Theme color tokens
  const cardBg = isDark ? '#141414' : '#FFFFFF';
  const textPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textSecondary = isDark ? '#94A3B8' : '#64748B';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.12)' : '#E2E8F0';
  const inputBg = isDark ? '#1E1E1E' : '#F8FAFC';
  const hoverBgSubtle = isDark ? 'rgba(255, 255, 255, 0.06)' : '#F1F5F9';

  // Form State
  const [selectedTests, setSelectedTests] = useState([]);
  const [customTestInput, setCustomTestInput] = useState('');
  const [customTests, setCustomTests] = useState([]);

  // Anatomic Region & Specific Site (Compulsory)
  const [selectedBodyRegion, setSelectedBodyRegion] = useState('');
  const [customBodyRegion, setCustomBodyRegion] = useState('');
  const [selectedSpecificSite, setSelectedSpecificSite] = useState('');
  const [customSpecificSite, setCustomSpecificSite] = useState('');

  // Additional fields
  const [urgency, setUrgency] = useState('routine');
  const [clinicalNotes, setClinicalNotes] = useState('');

  // Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Combined tests options
  const allTests = useMemo(() => {
    return [...PREDEFINED_TESTS, ...customTests];
  }, [customTests]);

  // Toggle test selection
  const handleToggleTest = (testValue) => {
    setSelectedTests(prev => {
      if (prev.includes(testValue)) {
        return prev.filter(t => t !== testValue);
      } else {
        return [...prev, testValue];
      }
    });
  };

  // Add custom test
  const handleAddCustomTest = () => {
    const trimmed = customTestInput.trim();
    if (!trimmed) return;
    if (!allTests.some(t => t.value.toLowerCase() === trimmed.toLowerCase())) {
      const newOption = { label: trimmed, value: trimmed };
      setCustomTests(prev => [...prev, newOption]);
      setSelectedTests(prev => [...prev, trimmed]);
    } else {
      if (!selectedTests.includes(trimmed)) {
        setSelectedTests(prev => [...prev, trimmed]);
      }
    }
    setCustomTestInput('');
  };

  // Handle Body Region change
  const handleBodyRegionChange = (regionKey) => {
    setSelectedBodyRegion(regionKey);
    setSelectedSpecificSite('');
    setCustomSpecificSite('');
    if (regionKey !== 'Other') {
      setCustomBodyRegion('');
    }
  };

  // Compulsory validation logic
  const isTestsValid = selectedTests.length > 0;
  const isBodyRegionValid = !!selectedBodyRegion && (selectedBodyRegion !== 'Other' || customBodyRegion.trim().length > 0);
  const isSpecificSiteValid = !!selectedSpecificSite && ((selectedSpecificSite !== 'Other' && selectedSpecificSite !== 'Specify Below') || customSpecificSite.trim().length > 0);
  const isFormValid = isTestsValid && isBodyRegionValid && isSpecificSiteValid;

  // Final strings for submission
  const finalBodyRegion = selectedBodyRegion === 'Other' ? customBodyRegion.trim() : selectedBodyRegion;
  const finalSpecificSite = (selectedSpecificSite === 'Other' || selectedSpecificSite === 'Specify Below')
    ? customSpecificSite.trim()
    : selectedSpecificSite;
  const combinedBodySite = finalBodyRegion ? `${finalBodyRegion}${finalSpecificSite ? ` - ${finalSpecificSite}` : ''}` : '';

  // Submit Handler
  const handleSubmit = async () => {
    if (!isFormValid) {
      if (!isTestsValid) setError('Please select at least one laboratory test');
      else if (!isBodyRegionValid) setError('Please select a body region');
      else if (!isSpecificSiteValid) setError('Please specify the exact anatomical site');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await referralService.requestLabTest({
        patientMrn: patientId,
        patientSnapshot: {
          mrn: patientId,
          firstName: patientName?.split(' ')[0] || '',
          lastName: patientName?.split(' ').slice(1).join(' ') || '',
        },
        requestedTests: selectedTests,
        clinicalNotes,
        urgency,
        bodySite: combinedBodySite,
        studyUID: studyUID || '',
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to send lab test request';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Reset & Close
  const handleClose = () => {
    setSelectedTests([]);
    setCustomTestInput('');
    setCustomTests([]);
    setSelectedBodyRegion('');
    setCustomBodyRegion('');
    setSelectedSpecificSite('');
    setCustomSpecificSite('');
    setUrgency('routine');
    setClinicalNotes('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: cardBg,
          color: textPrimary,
          borderRadius: '12px',
          border: `1px solid ${borderColor}`,
          boxShadow: isDark
            ? '0 20px 60px rgba(0,0,0,0.85)'
            : '0 20px 60px rgba(0,0,0,0.15)',
          backgroundImage: 'none',
          width: { xs: '95vw', sm: '92vw', md: '880px', lg: '920px' },
          maxWidth: '940px !important',
          maxHeight: '88vh',
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.25,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: isDark ? 'rgba(124, 58, 237, 0.15)' : '#F5F3FF',
              border: `1px solid ${isDark ? 'rgba(124, 58, 237, 0.3)' : '#DDD6FE'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#7C3AED',
            }}
          >
            <ScienceIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 800,
                fontSize: '1.05rem',
                color: textPrimary,
                lineHeight: 1.2,
              }}
            >
              Request Lab Test
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontSize: '0.72rem',
                color: textSecondary,
                fontWeight: 500,
              }}
            >
              PACS to LIS Pathology & Laboratory Referral
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: textSecondary,
            border: `1px solid ${borderColor}`,
            borderRadius: '6px',
            p: '3px',
            '&:hover': { bgcolor: hoverBgSubtle, color: textPrimary },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor }} />

      <DialogContent sx={{ px: 2, py: 1.25, overflowY: 'auto' }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#10B981',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                mb: 1.5,
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
              }}
            >
              <CheckIcon sx={{ fontSize: 32, strokeWidth: 1.5 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 800,
                fontSize: '1.2rem',
                color: textPrimary,
                mb: 0.75,
              }}
            >
              Lab Test Requested Successfully!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontSize: '0.85rem',
                color: textSecondary,
                maxWidth: 440,
                mx: 'auto',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              The referral order with target site <strong>{combinedBodySite}</strong> has been transmitted to the laboratory worklist.
            </Typography>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: '#005B4F',
                '&:hover': { bgcolor: '#004D47' },
                borderRadius: '8px',
                px: 3,
                height: '32px',
                fontSize: '0.82rem',
              }}
            >
              Done
            </Button>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert
                severity="error"
                sx={{
                  fontFamily: '"Satoshi", sans-serif',
                  borderRadius: '8px',
                  border: '1px solid #FCA5A5',
                  py: 0.25,
                  px: 1.25,
                  fontSize: '0.75rem',
                  mb: 1.25,
                }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
              {/* Left Column: Patient Details & Select Tests */}
              <Box display="flex" flexDirection="column" gap={1.25}>
                {/* Patient Context Card */}
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: '8px',
                    bgcolor: isDark ? 'rgba(13, 148, 136, 0.1)' : '#F0FDFA',
                    border: `1.5px solid ${isDark ? 'rgba(13, 148, 136, 0.25)' : '#CCFBF1'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: '#0D9488',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Patient Details
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontWeight: 800,
                        fontSize: '0.92rem',
                        color: textPrimary,
                      }}
                    >
                      {patientName || 'Unknown Patient'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.68rem', color: textSecondary, fontWeight: 600 }}>
                        MRN / ID
                      </Typography>
                      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.8rem', color: textPrimary }}>
                        {patientId || '—'}
                      </Typography>
                    </Box>
                    {studyUID && (
                      <Box>
                        <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.68rem', color: textSecondary, fontWeight: 600 }}>
                          Study UID
                        </Typography>
                        <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.8rem', color: textPrimary }}>
                          {studyUID.length > 14 ? `${studyUID.slice(0, 12)}...` : studyUID}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* SECTION 1: Select Tests */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: textPrimary,
                      }}
                    >
                      Select Tests <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: selectedTests.length > 0 ? '#10B981' : '#EF4444',
                      }}
                    >
                      {selectedTests.length > 0
                        ? `${selectedTests.length} test${selectedTests.length > 1 ? 's' : ''} selected`
                        : '* Select at least 1'}
                    </Typography>
                  </Box>

                  {/* Checkmark Pills Grid */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1 }}>
                    {allTests.map((t) => {
                      const isSelected = selectedTests.includes(t.value);
                      return (
                        <ButtonBase
                          key={t.value}
                          onClick={() => handleToggleTest(t.value)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            py: 0.4,
                            px: 1,
                            borderRadius: '16px',
                            border: '1.5px solid',
                            borderColor: isSelected
                              ? '#0D9488'
                              : isDark
                              ? 'rgba(255, 255, 255, 0.14)'
                              : '#CBD5E1',
                            bgcolor: isSelected
                              ? isDark
                                ? 'rgba(13, 148, 136, 0.2)'
                                : '#F0FDFA'
                              : isDark
                              ? '#1E1E1E'
                              : '#FFFFFF',
                            color: isSelected
                              ? isDark
                                ? '#2DD4BF'
                                : '#0F766E'
                              : isDark
                              ? '#CBD5E1'
                              : '#334155',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: '#0D9488',
                              bgcolor: isDark ? 'rgba(13, 148, 136, 0.12)' : '#F0FDFA',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 13,
                              height: 13,
                              borderRadius: '3px',
                              border: '1.2px solid',
                              borderColor: isSelected ? '#0D9488' : isDark ? '#64748B' : '#94A3B8',
                              bgcolor: isSelected ? '#0D9488' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#FFFFFF',
                              flexShrink: 0,
                            }}
                          >
                            {isSelected && <CheckIcon sx={{ fontSize: 10, stroke: '#FFFFFF', strokeWidth: 1.5 }} />}
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: '"Satoshi", sans-serif',
                              fontWeight: isSelected ? 700 : 500,
                              fontSize: '0.74rem',
                              userSelect: 'none',
                            }}
                          >
                            {t.label}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {/* Add Custom Test input */}
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <TextField
                      size="small"
                      placeholder="Type custom test name..."
                      value={customTestInput}
                      onChange={(e) => setCustomTestInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomTest();
                        }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: '0.78rem',
                          height: 30,
                          borderRadius: '8px',
                          bgcolor: inputBg,
                          color: textPrimary,
                          border: `1px solid ${borderColor}`,
                          '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputBase-input': {
                          color: textPrimary,
                        }
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleAddCustomTest}
                      disabled={!customTestInput.trim()}
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        minWidth: 90,
                        height: 30,
                        borderRadius: '8px',
                        fontFamily: '"Satoshi", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.74rem',
                        textTransform: 'none',
                        borderColor: '#0D9488',
                        color: '#0D9488',
                      }}
                    >
                      Add Test
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Right Column: Body Part, Specific Site, Urgency & Notes */}
              <Box display="flex" flexDirection="column" gap={1.25}>
                {/* SECTION 2: Body Part & Specific Region */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: textPrimary,
                      }}
                    >
                      Body Part & Specific Region <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Satoshi", sans-serif',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: isBodyRegionValid && isSpecificSiteValid ? '#10B981' : '#EF4444',
                      }}
                    >
                      {isBodyRegionValid && isSpecificSiteValid ? '✓ Specified' : '* Required'}
                    </Typography>
                  </Box>

                  {/* Body Region Pills */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 0.75 }}>
                    {Object.keys(ANATOMIC_REGIONS).map((regKey) => {
                      const isSelected = selectedBodyRegion === regKey;
                      return (
                        <ButtonBase
                          key={regKey}
                          onClick={() => handleBodyRegionChange(regKey)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.6,
                            py: 0.35,
                            px: 1,
                            borderRadius: '16px',
                            border: '1.2px solid',
                            borderColor: isSelected
                              ? '#2563EB'
                              : isDark
                              ? 'rgba(255, 255, 255, 0.14)'
                              : '#CBD5E1',
                            bgcolor: isSelected
                              ? isDark
                                ? 'rgba(37, 99, 235, 0.2)'
                                : '#EFF6FF'
                              : isDark
                              ? '#1E1E1E'
                              : '#FFFFFF',
                            color: isSelected
                              ? isDark
                                ? '#60A5FA'
                                : '#1E40AF'
                              : isDark
                              ? '#CBD5E1'
                              : '#334155',
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              border: '1.2px solid',
                              borderColor: isSelected ? '#2563EB' : isDark ? '#64748B' : '#94A3B8',
                              bgcolor: isSelected ? '#2563EB' : 'transparent',
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: '"Satoshi", sans-serif',
                              fontWeight: isSelected ? 700 : 500,
                              fontSize: '0.74rem',
                            }}
                          >
                            {ANATOMIC_REGIONS[regKey].label}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {selectedBodyRegion && ANATOMIC_REGIONS[selectedBodyRegion] && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                      {ANATOMIC_REGIONS[selectedBodyRegion].sites.map((site) => {
                        const isSelected = selectedSpecificSite === site;
                        return (
                          <ButtonBase
                            key={site}
                            onClick={() => setSelectedSpecificSite(site)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              py: 0.25,
                              px: 0.85,
                              borderRadius: '14px',
                              border: '1.2px solid',
                              borderColor: isSelected
                                ? '#00665C'
                                : isDark
                                ? 'rgba(255, 255, 255, 0.14)'
                                : '#CBD5E1',
                              bgcolor: isSelected
                                ? isDark
                                  ? 'rgba(0, 102, 92, 0.25)'
                                  : '#F0FDFA'
                                : isDark
                                ? '#1E1E1E'
                                : '#FFFFFF',
                              color: isSelected
                                ? isDark
                                  ? '#5EEAD4'
                                  : '#00665C'
                                : isDark
                                ? '#CBD5E1'
                                : '#334155',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: '"Satoshi", sans-serif',
                                fontWeight: isSelected ? 700 : 500,
                                fontSize: '0.72rem',
                              }}
                            >
                              {site}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  )}

                  {/* Underneath: Show Selected Target Site */}
                  {combinedBodySite && (
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: '6px',
                        bgcolor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#EFF6FF',
                        border: `1px solid ${isDark ? '#2563EB' : '#93C5FD'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <LocationOnIcon sx={{ color: '#2563EB', fontSize: 16 }} />
                      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.76rem', color: textPrimary }}>
                        Target: {combinedBodySite}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* SECTION 3: Urgency & Clinical Notes */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.8rem', color: textPrimary }}>
                      Urgency:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {URGENCY_OPTIONS.map((u) => {
                        const isSelected = urgency === u.value;
                        const isStat = u.value === 'stat';
                        const isUrgent = u.value === 'urgent';
                        const activeColor = isStat ? '#EF4444' : isUrgent ? '#F59E0B' : '#0D9488';
                        return (
                          <ButtonBase
                            key={u.value}
                            onClick={() => setUrgency(u.value)}
                            sx={{
                              py: 0.25,
                              px: 0.85,
                              borderRadius: '14px',
                              border: '1.2px solid',
                              borderColor: isSelected ? activeColor : borderColor,
                              bgcolor: isSelected ? `${activeColor}20` : 'transparent',
                              color: isSelected ? activeColor : textSecondary,
                            }}
                          >
                            <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: isSelected ? 700 : 500, fontSize: '0.72rem' }}>
                              {u.label}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Clinical Notes */}
                  <TextField
                    multiline
                    rows={2}
                    size="small"
                    value={clinicalNotes}
                    onChange={(e) => setClinicalNotes(e.target.value)}
                    placeholder="Clinical notes / Reason for lab request..."
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Satoshi", sans-serif',
                        fontSize: '0.78rem',
                        borderRadius: '8px',
                        bgcolor: inputBg,
                        color: textPrimary,
                        border: `1px solid ${borderColor}`,
                        '& fieldset': { border: 'none' },
                      },
                      '& .MuiInputBase-input': {
                        color: textPrimary,
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider sx={{ borderColor }} />

      {/* Dialog Footer Actions */}
      {!success && (
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: isDark ? '#141414' : '#F8FAFC',
          }}
        >
          {/* Compulsory summary checklist */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isFormValid ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <WarningIcon sx={{ color: '#F59E0B', fontSize: 18 }} />
                <Typography
                  sx={{
                    fontFamily: '"Satoshi", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#F59E0B',
                  }}
                >
                  Required before sending: {!isTestsValid ? '• 1+ Test ' : ''}{!isBodyRegionValid ? '• Body Part ' : ''}{!isSpecificSiteValid ? '• Specific Region' : ''}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#10B981',
                }}
              >
                ✓ All required information provided
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={handleClose}
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                color: textSecondary,
                borderRadius: '10px',
                px: 2.5,
                '&:hover': { bgcolor: hoverBgSubtle, color: textPrimary },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !isFormValid}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#005B4F',
                '&:hover': { bgcolor: '#004D47' },
                '&:disabled': {
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                  color: isDark ? 'rgba(255, 255, 255, 0.25)' : '#94A3B8',
                },
                px: 3.5,
                py: 1,
                fontSize: '0.88rem',
                boxShadow: 'none',
              }}
            >
              {loading ? 'Sending...' : 'Send to Laboratory'}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
